// Every timestamp in this app is UTC and labelled UTC (fleet frontend
// standard 6e). Never use getHours()/getDate()/toLocale*: those follow the
// viewer's browser time zone.

const pad = (n: number) => String(n).padStart(2, '0');

/** API timestamps may be naive UTC ("2026-10-08T14:02:00"): treat as UTC. */
export function parseUtc(iso: string): Date {
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
}

/** "2026-10-08 14:02" (UTC, no zone suffix: column headers carry "UTC"). */
export function formatUtc(iso: string | Date): string {
  const d = typeof iso === 'string' ? parseUtc(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

export function formatUtcDate(iso: string | Date): string {
  return formatUtc(iso).slice(0, 10);
}

/** ISO string with seconds, always ending in Z, for API query parameters. */
export function toApiUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`;
}

export function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export type WindowPreset = 'hour' | 'today' | 'week' | 'month' | 'quarter';

/** Preset windows, all cut on UTC boundaries ("Ma" = the current UTC day). */
export function presetWindow(preset: WindowPreset, now: Date): { from: Date; to: Date } {
  switch (preset) {
    case 'hour':
      return { from: new Date(now.getTime() - 3_600_000), to: now };
    case 'today':
      return { from: startOfUtcDay(now), to: now };
    case 'week':
      return { from: new Date(startOfUtcDay(now).getTime() - 6 * 86_400_000), to: now };
    case 'month':
      return { from: new Date(startOfUtcDay(now).getTime() - 29 * 86_400_000), to: now };
    case 'quarter':
      return { from: new Date(startOfUtcDay(now).getTime() - 89 * 86_400_000), to: now };
  }
}

/** "YYYY-MM-DDTHH:MM" (datetime-local value, interpreted as UTC) -> Date. */
export function parseUtcInput(v: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?$/.test(v.trim())) return null;
  const d = parseUtc(v.trim().replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function utcInputValue(d: Date): string {
  return toApiUtc(d).slice(0, 16).replace('T', ' ');
}

/** Whole days between two instants, from the UTC clock (floor). */
export function daysBetween(fromIso: string, to: Date): number {
  return Math.max(0, Math.floor((to.getTime() - parseUtc(fromIso).getTime()) / 86_400_000));
}
